import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

// User interface based on the API response
export interface User {
  id: string;
  name: string;
  email_id: string;
  role: string;
  is_active: boolean;
  last_login?: string;
  created_datetime: string;
  updated_datetime: string;
  company_id: string;
}

export interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  size: number;
}

export interface CreateUserRequest {
  name: string;
  email_id: string;
  role: string;
  company_id: string;
}

export interface UpdateUserRequest {
  role: string;
  is_active: boolean;
}

export interface DeleteUserResponse {
  success: boolean;
  message: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  company_id: string;
  invited_by: { userId: string; userEmail: string; username: string; role: string; } | null;
  status: string;
  message: string | null;
  created_at: string;
  expires_at: string;
}

export interface AccessRequest {
  id: string;
  email: string;
  company_id: string;
  status: string;
  requested_at: string;
  reviewed_at?: string | null;
  reviewed_by?: {
    userId: string;
    userEmail?: string | null;
    username?: string | null;
    role?: string | null;
  } | null;
  message?: string | null;
  source?: string | null;
}

export interface AccessRequestsResponse {
  access_requests: AccessRequest[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface PendingInvitationsResponse {
  invitations: PendingInvitation[];
  total: number;
  page: number;
  size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface BulkInviteRequest {
  users: Array<{
    email: string;
    company_id: string;
    role: string;
  }>;
  user_id: string;
  platform_name: string;
}

export interface BulkInviteResponse {
  message: string;
  total_invited: number;
  successful_invitations: Array<{
    message: string;
    invite_id: string;
    email_id: string;
    registration_link: string;
    expires_at: string;
  }>;
  failed_invitations: Array<{
    email_id: string;
    reason: string;
  }>;
}

/**
 * Reads the backend's actionable detail message off a failed approve/reject-access-request
 * response (shay-suite-backend#455 returns e.g. 404 "Access request not found", 400 "Access
 * request is not pending" — the exact race when two admins act on the same request, or a
 * double-click races itself — and 403 "Insufficient permissions"). Falls back to the generic
 * HTTP status message if the body isn't the expected shape or fails to parse.
 */
async function extractAccessRequestErrorMessage(response: Response): Promise<string> {
  try {
    const errorData = await response.json();
    const detail = errorData?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const msg = typeof first === 'string' ? first : first?.msg;
      if (typeof msg === 'string' && msg.trim()) {
        return msg;
      }
    }
  } catch {
    // Body wasn't JSON, or was empty — fall through to the generic message below.
  }
  return `HTTP error! status: ${response.status}`;
}

class UserService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    return {
      'Content-Type': 'application/json',
      'accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  async getCompanyUsers(
    companyId: string, 
    page: number = 1, 
    size: number = 10, 
    search?: string, 
    role?: string, 
    status?: string
  ): Promise<UsersResponse> {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Add role filter if provided
      if (role && role.trim()) {
        params.append('role', role.trim());
      }
      
      // Add status filter if provided
      if (status && status.trim()) {
        params.append('status', status.trim());
      }
      
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.LIST_COMPANY_USERS(companyId)}?${params}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching company users:', error);
      throw error;
    }
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.CREATE_USER}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(userData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(userId: string, userData: UpdateUserRequest, companyId: string): Promise<User> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.UPDATE_USER(userId, companyId)}`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(userData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string, companyId: string): Promise<DeleteUserResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.DELETE_USER(userId, companyId)}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.USER_BY_ID(userId)}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async getPendingInvitations(
    companyId: string,
    page: number = 1,
    size: number = 10,
    search?: string,
    role?: string
  ): Promise<PendingInvitationsResponse> {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.append('search', search.trim());
      }
      
      // Add role filter if provided
      if (role && role.trim()) {
        params.append('role', role.trim());
      }

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.GET_COMPANY_INVITATIONS(companyId)}?${params}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both array (legacy) and object response formats
      if (Array.isArray(data)) {
        return {
          invitations: data,
          total: data.length,
          page: 1,
          size: data.length,
          total_pages: 1,
          has_next: false,
          has_previous: false
        };
      }
      
      // Ensure invitations is always an array even if response structure is partial
      if (!data.invitations) {
        data.invitations = [];
      }
      
      return data as PendingInvitationsResponse;
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
      throw error;
    }
  }

  async getAccessRequests(
    companyId: string,
    page: number = 1,
    size: number = 10,
    search?: string,
    status?: string,
    sortBy: string = 'requested_at',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<AccessRequestsResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      if (status && status.trim()) {
        params.append('status', status.trim());
      }

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.GET_COMPANY_ACCESS_REQUESTS(companyId)}?${params}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        return {
          access_requests: data,
          total: data.length,
          page: 1,
          size: data.length,
          total_pages: 1,
          has_next: false,
          has_previous: false,
        };
      }

      if (!data.access_requests) {
        data.access_requests = [];
      }

      return data as AccessRequestsResponse;
    } catch (error) {
      console.error('Error fetching access requests:', error);
      throw error;
    }
  }

  async approveAccessRequest(
    companyId: string,
    requestId: string,
    role: 'admin' | 'user' = 'user'
  ): Promise<{ message: string; request: AccessRequest }> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.APPROVE_ACCESS_REQUEST(companyId, requestId)}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ role }),
        }
      );

      if (!response.ok) {
        throw new Error(await extractAccessRequestErrorMessage(response));
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving access request:', error);
      throw error;
    }
  }

  async rejectAccessRequest(companyId: string, requestId: string): Promise<{ message: string; request: AccessRequest }> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.REJECT_ACCESS_REQUEST(companyId, requestId)}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(await extractAccessRequestErrorMessage(response));
      }

      return await response.json();
    } catch (error) {
      console.error('Error rejecting access request:', error);
      throw error;
    }
  }

  async resendInvitation(data: BulkInviteRequest): Promise<BulkInviteResponse> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USER_AUTH.BULK_INVITE}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  }

  async deletePendingInvitation(companyId: string, email: string): Promise<{
    message: string;
    deleted_count: number;
    email: string;
    company_id: string;
  }> {
    try {
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USERS.DELETE_PENDING_INVITATIONS(companyId, email)}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting pending invitation:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
export default userService;
